import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppModal from '../components/AppModal';
import Breadcrumb from '../components/Breadcrumb';
import { API_BASE_URL } from '../api';
import { canWriteTestCases } from '../permissions';
import './TestExecutionPage.css';

/**
 * Read-only detail of a single test case — the same information shown while
 * executing a run (name, priority, category, preconditions, description,
 * ordered steps, expected result).
 */
function TestCaseDetailPage({ user }) {
  const { projectId, suiteId, caseId } = useParams();
  const navigate = useNavigate();
  const canWrite = canWriteTestCases(user);
  const token = localStorage.getItem('token');

  const [project, setProject] = useState(null);
  const [suite, setSuite] = useState(null);
  const [testCase, setTestCase] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deletingRef = useRef(false);

  const suitePath = `/projects/${projectId}/suites/${suiteId}`;

  useEffect(() => {
    let cancelled = false;
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    fetch(`${API_BASE_URL}/projects/${projectId}`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => { if (!cancelled && p) setProject(p); })
      .catch(() => {});
    fetch(`${API_BASE_URL}/suites/${suiteId}`, auth)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => { if (!cancelled && s) setSuite(s); })
      .catch(() => {});

    fetch(`${API_BASE_URL}/testcases/${caseId}`, auth)
      .then((response) => {
        if (cancelled) return null;
        if (response.status === 404) { setStatus('notfound'); return null; }
        if (!response.ok) { setStatus('error'); return null; }
        return response.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        setTestCase(data);
        setStatus('ok');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => { cancelled = true; };
  }, [projectId, suiteId, caseId, token]);

  const handleDelete = async () => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      const response = await fetch(`${API_BASE_URL}/testcases/${caseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        navigate(suitePath);
        return;
      }
      const data = await response.json().catch(() => ({}));
      setDeleteError(data.error || 'Failed to delete the test case.');
    } catch (err) {
      setDeleteError('Failed to delete the test case: ' + err.message);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
      setPendingDelete(false);
    }
  };

  return (
    <div className="test-execution-page">
      <div className="container">
        <Breadcrumb
          items={[
            { label: 'Projects', to: '/projects' },
            { label: project?.name || `Project ${projectId}`, to: `/projects/${projectId}` },
            { label: suite?.name || `Suite ${suiteId}`, to: suitePath },
            { label: testCase?.name || `Case ${caseId}` },
          ]}
        />

        {status === 'loading' && <p className="help-text">Loading test case…</p>}
        {status === 'notfound' && (
          <div className="empty-state"><p>This test case could not be found.</p></div>
        )}
        {status === 'error' && (
          <div className="empty-state"><p>Something went wrong loading this test case. Try again.</p></div>
        )}

        {status === 'ok' && testCase && (
          <>
            <div className="results-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h1>{testCase.name}</h1>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-secondary" onClick={() => navigate(suitePath)}>← Back</button>
                {canWrite && (
                  <>
                    <button className="btn" onClick={() => navigate(`${suitePath}/cases/${caseId}/edit`)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => setPendingDelete(true)}>Delete</button>
                  </>
                )}
              </div>
            </div>

            <div className="test-details card" style={{ marginTop: '16px' }}>
              <p><strong>Priority:</strong> {testCase.priority}</p>
              <p><strong>Category:</strong> {testCase.category || 'N/A'}</p>

              {testCase.preconditions && (
                <div>
                  <strong>Preconditions:</strong>
                  <p>{testCase.preconditions}</p>
                </div>
              )}

              {testCase.description && (
                <div>
                  <strong>Description:</strong>
                  <p>{testCase.description}</p>
                </div>
              )}

              {testCase.steps && testCase.steps.length > 0 && (
                <div>
                  <strong>Test Steps:</strong>
                  <ol>
                    {testCase.steps.map((step) => (
                      <li key={step.id}>{step.description}</li>
                    ))}
                  </ol>
                </div>
              )}

              {testCase.expected_result && (
                <div>
                  <strong>Expected Result:</strong>
                  <p>{testCase.expected_result}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {pendingDelete && (
        <AppModal
          open={pendingDelete}
          title="Delete test case?"
          message={deleteError || 'This action cannot be undone.'}
          onClose={() => { setPendingDelete(false); setDeleteError(''); }}
          actions={[
            { label: 'Cancel', variant: 'secondary', onClick: () => { setPendingDelete(false); setDeleteError(''); }, disabled: deleting },
            { label: deleting ? 'Deleting...' : 'Delete', variant: 'danger', onClick: handleDelete, disabled: deleting },
          ]}
        />
      )}
    </div>
  );
}

export default TestCaseDetailPage;
